import { Trans, useTranslation } from 'react-i18next'
import { Alert } from '@cmsgov/ds-medicare-gov'
import Skeleton from 'react-loading-skeleton'
import { SplitPageLayout } from 'containers/Premiums/components/SplitPageLayout/SplitPageLayout'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { useFetchPendingPayments, useFetchPremiumSummary, useStartPayment } from 'queries/premiums'
import { ContentLayout } from 'components/Layouts'
import { BackToPremiumsLink } from 'containers/Premiums/components/BackToPremiums'
import { BackToPremiumsBreadcrumb } from 'containers/Premiums/components/BackToPremiumsBreadcrumb'
import RecentPaymentAlert from 'containers/Premiums/components/makeAPayment/RecentPaymentAlert'
import IneligibleAlert from 'containers/Premiums/components/makeAPayment/IneligibleAlert'
import EasyPaySignupAlert from '../../components/makeAPayment/EasyPaySignupAlert'
import EasyPayEnrolledAlert from '../../components/makeAPayment/EasyPayEnrolledAlert'
import MakeAPaymentForm from 'containers/Premiums/components/makeAPayment/MakeAPaymentForm/MakeAPaymentForm'
import { MaPartDIrmaaCardStack } from 'containers/Premiums/components/makeAPayment/MaPartDIrmaaCardStack'
import { MakeAPaymentFormData } from 'containers/Premiums/components/makeAPayment/MakeAPaymentForm/schema'
import { useEvent } from 'hooks/useEvent'
import { Component } from '@cms-max/medicare-api-sdk'
import { useMedicareUrl } from 'hooks/useMedicareUrl'

export function MakeAPaymentPage () {
  const { t, i18n } = useTranslation('makeAPaymentPage')
  const lang = i18n.language as 'en' | 'es'
  const { data, isError, isLoading, isSuccess } = useFetchPremiumSummary()
  const { data: pendingPayments } = useFetchPendingPayments()
  const { fireEvent } = useEvent('premiumsMakeAPayment')
  const hasRecentPayment = pendingPayments && pendingPayments?.length > 0

  const otherWaysLink = useMedicareUrl('https://www.medicare.gov/basics/costs/pay-premiums', 'https://es.medicare.gov/basics/costs/pay-premiums')

  const { isEasyPayEnrolled, isDirectBillingEligible, beneMessages } = data ?? {}

  const { mutateAsync: startPayment, isLoading: isSubmitting, isError: isMutationError } = useStartPayment()

  const handleSubmit = async ({ amount, paymentMethod }: MakeAPaymentFormData) => {
    try {
      // Perform mutation to CORE and receive token & app ID
      const { token, tcsAppId } = await startPayment({
        amount: parseFloat(amount),
        paymentMethod
      })

      // Perform redirect to Pay.gov
      window.location.assign(`${process.env.REACT_APP_PAYGOV_BASE_URL}/tcsonline/payment.do?token=${token}&tcsAppID=${tcsAppId}`)
    } catch (error) {
      console.error(error)
    }
  }

  const baseLayoutProps = {
    heading: t('heading'),
    width: 'wide',
    nav: <BackToPremiumsBreadcrumb />,
    printComponent: Component.Premiums
  } as const

  // We are being explicit about the type here to ensure we handle `undefined` and `false` separately and correctly
  if (isSuccess && isDirectBillingEligible === false) {
    return (
      <ContentLayout
        {...baseLayoutProps}
      >
        <div className='ds-l-container'>
          <IneligibleAlert deferred={data?.isDirectBillingDeferred} />
          <div className='ds-u-margin-top--5'>
            <BackToPremiumsLink />
          </div>
        </div>
      </ContentLayout>
    )
  }

  // We have extra check here to ensure that if we have an successful-but-empty (204) response, we still show error
  if (isError || isMutationError || (isSuccess && !isDirectBillingEligible)) {
    return (
      <ContentLayout
        {...baseLayoutProps}
      >
        <div className='ds-l-container'>
          <Alert variation='error' heading={t('serverErrorAlert.heading')} className='ds-u-measure--wide'>{t('serverErrorAlert.content')}</Alert>
          <div className='ds-u-margin-top--5'>
            <BackToPremiumsLink />
          </div>
        </div>
      </ContentLayout>
    )
  }

  return (
    <SplitPageLayout
      {...baseLayoutProps}
      left={
        <>
          <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>
            {isLoading ? <Skeleton width={353} /> : t('beforeYouPay.heading')}
          </h2>
          {isLoading && <Skeleton height={253} />}
          {!isLoading && (
            <>
              <Trans t={t} i18nKey='beforeYouPay.content' />
              <MyLink href={otherWaysLink}
                onClick={({ linkText, linkUrl }) =>
                  fireEvent('premiums_makeapayment_otherwaystopay_click', {
                    linkText,
                    linkUrl
                  })}>
                {t('beforeYouPay.otherWaysLink')}
              </MyLink>
              <MaPartDIrmaaCardStack isPartCorD={data?.isPartCOrD} isPartDIRMAA={data?.isPartDIRMAA} className='ds-u-margin-top--3' />
            </>
          )}
        </>
      }
      right={
        <>
          <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>
            {isLoading ? <Skeleton width={353} /> : t('payYourPremium.heading')}
          </h2>
          {isLoading && <Skeleton height={760} />}

          {!isLoading && (
            <>
              {hasRecentPayment && <RecentPaymentAlert />}
              <div className="ds-u-margin-bottom--3">
                {isEasyPayEnrolled ? <EasyPayEnrolledAlert /> : <EasyPaySignupAlert />}
              </div>
              <Trans t={t} i18nKey='payYourPremium.paymentInfo' />

              {beneMessages?.lastBill && (
                <p>{beneMessages.lastBill[lang]}</p>
              )}

              {beneMessages?.paymentProcessed && (
                <p>
                  {beneMessages.paymentProcessed[lang]}{' '}
                  <MyInternalLink
                    to="/premiums/history"
                    onClick={({ linkText, linkUrl }) => {
                      fireEvent('premiums_makeapayment_checkhistory_click', {
                        linkText,
                        linkUrl
                      })
                    }}>
                      {t('payYourPremium.checkPaymentHistoryLink')}
                  </MyInternalLink>
                </p>
              )}

              {beneMessages?.noPremiumOwed && (
                <p>{beneMessages.noPremiumOwed[lang]}</p>
              )}

              <MakeAPaymentForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
            </>
          )}
        </>
      }
    />
  )
}

export default MakeAPaymentPage
